/**
 * Privacy policy copy — the live page's own `privacy.*` translation keys.
 *
 * ⚠️  THIS MODULE HOLDS KEYS, NOT ENGLISH. Every string below is a translation key
 * that LegalDoc resolves (see @Components/LegalDoc/types).
 *
 * ── Why keys and not the transcribed text ───────────────────────────────────
 * It used to hold the English transcribed from Figma node 2066:27. That same document
 * is stored in Tolgee under `privacy.*` — already reviewed in all seven languages — so
 * this page reads those entries rather than carrying a second `privacy.v2.*` copy of a
 * legal document nobody wants to keep in step twice.
 *
 * ── What that decides about the structure ──────────────────────────────────
 * Run splits follow the stored keys, which is often FINER than the transcription was:
 *
 *   · The preamble's first paragraph is five runs (`intro.we`, the company name,
 *     `intro.hereinafter`, the address, `intro.datacontroller.*`) where the Figma text
 *     was one sentence. That is the stored granularity, and it is also more faithful —
 *     the company name and registered address are not translated.
 *   · The site URL in `privacy.intro.protection` stays inside the paragraph; LegalDoc's
 *     AUTOLINKS pass gives it its anchor back without cutting the sentence up.
 *   · Clause 2 is ONE stored key covering what the transcription split into two
 *     paragraphs, so it renders as one — matching the live page.
 *
 * Three places needed new `privacy.v2.*` keys, all because the design changes the SHAPE
 * of stored copy rather than its wording:
 *
 *   · Clause 1's definitions. The stored values carry their own "(a) " marker inline;
 *     LegalDoc renders markers in a gutter, so `privacy.v2.section1.def.*` is the same
 *     reviewed text per locale with the marker stripped.
 *   · Clause 3's bullets. Stored as one run-on sentence in `section3.paragraph2`; the
 *     design lists them, so each is its own key — plus `…section3.outro` for the
 *     trailing "advertising networks" sentence that was inside the same run.
 *   · Clause titles, which drop their number and trailing colon and are title-cased.
 */

import { Inline, InlineLink, LegalDocContent, Section } from "@Components/LegalDoc/types";

/** Contact points, stored verbatim — none of these are translated. */
const PHONE: InlineLink = { text: "+44 7456741634", href: "tel:+447456741634" };
const EMAIL: InlineLink = {
  text: "contact@amberstudent.com",
  href: "mailto:contact@amberstudent.com",
};
const COMPANY = "Amber Internet solutions Inc.";
const ADDRESS = "40 E Main St #1215 Newark DE, USA";

const INTRO: Inline[][] = [
  [
    "privacy.intro.we",
    ` ${COMPANY} `,
    "privacy.intro.hereinafter",
    ` ${ADDRESS} `,
    "privacy.intro.datacontroller.before",
    ` ${COMPANY} `,
    "privacy.intro.datacontroller.after",
  ],
  // The site URL inside this paragraph is linked by LegalDoc's AUTOLINKS pass.
  ["privacy.intro.protection"],
  ["privacy.intro.queries.before", " ", PHONE, " ", "privacy.intro.queries.email", " ", EMAIL, "."],
];

const CALLOUT = "privacy.intro.capitalized";

const SECTIONS: Section[] = [
  {
    title: "privacy.v2.section1.title",
    blocks: [
      {
        kind: "list",
        markers: "letters",
        items: [
          ["privacy.v2.section1.def.a"],
          ["privacy.v2.section1.def.b"],
          ["privacy.v2.section1.def.c"],
          ["privacy.v2.section1.def.d"],
          ["privacy.v2.section1.def.e"],
        ],
      },
    ],
  },
  {
    title: "privacy.v2.section2.title",
    blocks: [{ kind: "para", content: ["privacy.section2.content"] }],
  },
  {
    title: "privacy.v2.section3.title",
    blocks: [
      { kind: "para", content: ["privacy.section3.paragraph1"] },
      {
        kind: "list",
        markers: "bullets",
        items: [
          ["privacy.v2.section3.item1"],
          ["privacy.v2.section3.item2"],
          ["privacy.v2.section3.item3"],
          ["privacy.v2.section3.item4"],
          ["privacy.v2.section3.item5"],
          ["privacy.v2.section3.item6"],
        ],
      },
      { kind: "para", content: ["privacy.v2.section3.outro"] },
    ],
  },
  {
    title: "privacy.v2.section4.title",
    blocks: [
      { kind: "para", content: ["privacy.section4.paragraph1"] },
      { kind: "para", content: ["privacy.section4.paragraph2"] },
    ],
  },
  {
    title: "privacy.v2.section5.title",
    blocks: [
      { kind: "para", content: ["privacy.section5.paragraph1"] },
      { kind: "para", content: ["privacy.section5.paragraph2"] },
      { kind: "para", content: ["privacy.section5.paragraph3"] },
      { kind: "para", content: ["privacy.section5.paragraph4"] },
    ],
  },
  {
    title: "privacy.v2.section6.title",
    blocks: [
      {
        kind: "list",
        markers: "bullets",
        items: [
          ["privacy.section6.item1.before", " ", "privacy.section6.item1.bold"],
          ["privacy.section6.item2"],
          ["privacy.section6.item3"],
          ["privacy.section6.item4"],
        ],
      },
    ],
  },
  {
    title: "privacy.v2.section7.title",
    blocks: [
      {
        kind: "para",
        content: [
          "privacy.section7.paragraph1.before",
          " ",
          EMAIL,
          " ",
          "privacy.section7.paragraph1.after",
          " ",
          PHONE,
          ". ",
          "privacy.section7.paragraph1.correct",
        ],
      },
      {
        kind: "para",
        content: [
          "privacy.section7.marketing.bold",
          " ",
          "privacy.section7.marketing.contact",
          " ",
          EMAIL,
          " ",
          "privacy.section7.marketing.optout",
        ],
      },
      {
        kind: "para",
        content: [
          "privacy.section7.access.bold",
          ". ",
          "privacy.section7.access.email",
          " ",
          EMAIL,
          " ",
          "privacy.section7.access.free",
        ],
      },
      {
        kind: "para",
        content: [
          "privacy.section7.delete.bold",
          " ",
          "privacy.section7.delete.email",
          " ",
          EMAIL,
          " ",
          "privacy.section7.delete.exercise",
        ],
      },
    ],
  },
  {
    title: "privacy.v2.section8.title",
    blocks: [
      { kind: "para", content: ["privacy.section8.paragraph1"] },
      { kind: "para", content: ["privacy.section8.paragraph2"] },
      { kind: "para", content: ["privacy.section8.paragraph3"] },
    ],
  },
  {
    title: "privacy.v2.section9.title",
    blocks: [{ kind: "para", content: ["privacy.section9.content"] }],
  },
  {
    title: "privacy.v2.section10.title",
    blocks: [{ kind: "para", content: ["privacy.section10.content"] }],
  },
];

const PRIVACY_CONTENT: LegalDocContent = {
  intro: INTRO,
  callout: CALLOUT,
  sections: SECTIONS,
};

export default PRIVACY_CONTENT;
